import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText,
  Plane,
  Download,
  CheckCircle,
  Clock,
  AlertTriangle,
  User,
  Users,
  Building2,
  Receipt,
  ArrowLeft,
  Edit,
  XCircle,
  Wallet,
  Paperclip,
  CheckCircle2,
  DollarSign,
  Send,
  Upload,
  Eye,
  Image as ImageIcon,
  UploadCloud,
  Loader2,
  Trash2,
  Link2,
  ExternalLink,
  Plus,
  FileSpreadsheet,
  AlertCircle,
  RotateCcw,
  Save,
  Calendar,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';
import AttachmentPreviewModal from '../../components/travel/AttachmentPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelRequestDetail = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Finance Advance Payment State
  const [advPaymentForm, setAdvPaymentForm] = useState({
    payment_status: 'paid',
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: '',
    paid_amount: '',
    finance_remarks: '',
  });
  const [processingAdvPayment, setProcessingAdvPayment] = useState(false);

  // Employee Settlement Submission Form State
  const [settlementForm, setSettlementForm] = useState({
    actual_hotel: '',
    actual_food: '',
    actual_local_travel: '',
    actual_misc: '',
    actual_ticket: '',
    actual_other: '',
    employee_notes: '',
    attachments: [],
    google_drive_links: [],
  });
  const [driveLinkInput, setDriveLinkInput] = useState('');
  const [uploadingBills, setUploadingBills] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadError, setUploadError] = useState(null);
  const [submittingSettlement, setSubmittingSettlement] = useState(false);

  // Finance Settlement Verification State
  const [finSettlementForm, setFinSettlementForm] = useState({
    approved_hotel: '',
    approved_food: '',
    approved_local_travel: '',
    approved_misc: '',
    payment_reference: '',
    finance_remarks: '',
  });
  const [processingFinSettlement, setProcessingFinSettlement] = useState(false);

  // Unified Outstation Travel Management State
  const [travelDailyData, setTravelDailyData] = useState(null);
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    expense_category: 'Food & Meals',
    description: '',
    location: '',
    amount_spent: '',
    paid_by: 'Employee',
    payment_mode: 'Cash/UPI',
    advance_ref_no: '',
    remarks: '',
    bill_attachments: [],
  });
  const [savingExpense, setSavingExpense] = useState(false);

  // Additional Advance Modal State
  const [showAddAdvanceModal, setShowAddAdvanceModal] = useState(false);
  const [addAdvanceForm, setAddAdvanceForm] = useState({
    requested_amount: '',
    remarks: '',
    hotel_amount: '',
    food_amount: '',
    local_travel_amount: '',
    misc_amount: '',
  });
  const [submittingAddAdvance, setSubmittingAddAdvance] = useState(false);

  // Advance Action (Approval/Rejection) Modal State
  const [selectedAdvanceForAction, setSelectedAdvanceForAction] = useState(null);
  const [advanceActionForm, setAdvanceActionForm] = useState({
    action: 'approve',
    approved_amount: '',
    remarks: '',
  });
  const [submittingAdvanceAction, setSubmittingAdvanceAction] = useState(false);

  // Advance Disbursement Modal State
  const [selectedAdvanceForDisburse, setSelectedAdvanceForDisburse] = useState(null);
  const [advanceDisburseForm, setAdvanceDisburseForm] = useState({
    payment_reference: '',
    paid_amount: '',
    payment_date: new Date().toISOString().split('T')[0],
  });
  const [submittingAdvanceDisburse, setSubmittingAdvanceDisburse] = useState(false);

  // Extension Modal State
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendForm, setExtendForm] = useState({
    revised_end_date: '',
    reason: '',
    additional_advance_amount: '',
  });
  const [submittingExtend, setSubmittingExtend] = useState(false);

  // Consolidated Final Claim Modal State
  const [showFinalClaimModal, setShowFinalClaimModal] = useState(false);
  const [submittingFinalClaim, setSubmittingFinalClaim] = useState(false);

  useEffect(() => {
    fetchRequestDetail();
  }, [id]);

  const fetchRequestDetail = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/requests/${id}`, { withCredentials: true });
      const req = data.request;
      setRequest(req);

      // Fetch unified daily expenses & dashboard metrics
      try {
        const { data: dailyData } = await axios.get(`${API_URL}/travel-desk/requests/${id}/daily-expenses`, { withCredentials: true });
        setTravelDailyData(dailyData);
      } catch (e) {
        console.error('Error fetching daily expenses:', e);
      }

      // Pre-fill finance advance payment form
      if (req?.advance_details || req?.requested_advance_amount) {
        setAdvPaymentForm((prev) => ({
          ...prev,
          paid_amount: req.advance_payment?.paid_amount || req.approved_advance_amount || req.advance_details?.approved_advance_amount || req.requested_advance_amount || req.advance_details?.total_advance || '',
          payment_reference: req.advance_payment?.payment_reference || '',
          finance_remarks: req.advance_payment?.finance_remarks || '',
        }));
      }

      // Pre-fill finance settlement verification form if settlement exists
      if (req?.settlement && req.settlement.settlement_status !== 'not_submitted') {
        setFinSettlementForm({
          approved_hotel: req.settlement.approved_hotel || req.settlement.actual_hotel || 0,
          approved_food: req.settlement.approved_food || req.settlement.actual_food || 0,
          approved_local_travel: req.settlement.approved_local_travel || req.settlement.actual_local_travel || 0,
          approved_misc: req.settlement.approved_misc || req.settlement.actual_misc || 0,
          payment_reference: req.settlement.payment_reference || '',
          finance_remarks: req.settlement.finance_remarks || '',
        });
      }

      // Pre-fill employee settlement form if settlement exists
      if (req?.settlement) {
        const rawDriveLinks = req.settlement.google_drive_links || (req.settlement.google_drive_link ? [req.settlement.google_drive_link] : []);
        setSettlementForm((prev) => ({
          ...prev,
          actual_hotel: req.settlement.actual_hotel ?? '',
          actual_food: req.settlement.actual_food ?? '',
          actual_local_travel: req.settlement.actual_local_travel ?? '',
          actual_misc: req.settlement.actual_misc ?? '',
          actual_ticket: req.settlement.actual_ticket ?? '',
          actual_other: req.settlement.actual_other ?? '',
          employee_notes: req.settlement.employee_notes || '',
          attachments: Array.isArray(req.settlement.attachments) ? req.settlement.attachments : [],
          google_drive_links: Array.isArray(rawDriveLinks) ? rawDriveLinks : [rawDriveLinks].filter(Boolean),
        }));
      }
    } catch (err) {
      toast.error('Failed to load travel request details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      const response = await axios.get(`${API_URL}/travel-desk/requests/${id}/receipt`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TravelReceipt_${request?.request_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Travel Approval Receipt downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download receipt PDF');
    }
  };

  // Submit Advance Payment Action (Finance)
  const handleAdvancePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      setProcessingAdvPayment(true);
      await axios.post(`${API_URL}/travel-desk/requests/${id}/advance-payment`, advPaymentForm, {
        withCredentials: true,
      });
      toast.success(`Travel advance payment updated to ${advPaymentForm.payment_status.toUpperCase()}`);
      fetchRequestDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update advance payment status');
    } finally {
      setProcessingAdvPayment(false);
    }
  };

  // Google Drive Link Handler
  const handleAddDriveLink = () => {
    let link = (driveLinkInput || '').trim();
    if (!link) {
      toast.error('Please enter a Google Drive link.');
      return;
    }

    if (!link.startsWith('http://') && !link.startsWith('https://')) {
      if (link.includes('drive.google.com') || link.includes('docs.google.com')) {
        link = `https://${link}`;
      } else {
        toast.error('Please enter a valid URL starting with https://');
        return;
      }
    }

    try {
      const parsed = new URL(link);
      const host = parsed.hostname.toLowerCase();
      if (!host.includes('drive.google.com') && !host.includes('docs.google.com')) {
        toast.error('Link must be a valid Google Drive or Google Docs URL (e.g. drive.google.com/...).');
        return;
      }
    } catch (_) {
      toast.error('Invalid URL format. Please paste a valid web address.');
      return;
    }

    const currentLinks = settlementForm.google_drive_links || [];
    if (currentLinks.includes(link)) {
      toast.info('This Google Drive link has already been added.');
      setDriveLinkInput('');
      return;
    }

    setSettlementForm((prev) => ({
      ...prev,
      google_drive_links: [...(prev.google_drive_links || []), link],
    }));
    setDriveLinkInput('');
    toast.success('Google Drive link added.');
  };

  const handleRemoveDriveLink = (index) => {
    setSettlementForm((prev) => ({
      ...prev,
      google_drive_links: (prev.google_drive_links || []).filter((_, i) => i !== index),
    }));
  };

  // Supporting Document Bills Upload Handler
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploadingBills(true);
    setUploadError(null);
    setUploadProgress(`Processing ${files.length} file(s)...`);

    const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'xls', 'xlsx'];
    const maxSizeBytes = 20 * 1024 * 1024; // 20 MB

    const newAttachments = [...(settlementForm.attachments || [])];
    let failureCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.split('.').pop().toLowerCase();
      setUploadProgress(`Uploading ${file.name} (${i + 1}/${files.length})...`);

      if (!allowedExts.includes(ext)) {
        toast.error(`"${file.name}" has an unsupported format. Please upload PDF, JPG, JPEG, PNG, XLS, or XLSX.`);
        failureCount++;
        continue;
      }

      if (file.size > maxSizeBytes) {
        toast.error(`"${file.name}" exceeds the 20 MB limit (${(file.size / (1024 * 1024)).toFixed(1)} MB).`);
        failureCount++;
        continue;
      }

      // Convert to base64 for fallback preview
      const base64DataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      });

      let uploadedData = null;
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API_URL}/travel-desk/upload-attachment`, fd, {
          withCredentials: true,
        });
        uploadedData = data;
      } catch (err) {
        console.warn('Backend attachment upload endpoint failed, falling back to base64 payload', err);
        const errMsg = err.response?.data?.detail || err.message || 'Upload failed';
        if (err.response?.status === 400 && errMsg.includes('File type not supported')) {
          toast.error(`File rejected by server: ${errMsg}`);
          failureCount++;
          continue;
        }
      }

      const sizeFormatted = file.size >= 1024 * 1024
        ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.max(1, Math.round(file.size / 1024))} KB`;

      let mimeType = file.type;
      if (!mimeType) {
        if (ext === 'pdf') mimeType = 'application/pdf';
        else if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
        else if (ext === 'png') mimeType = 'image/png';
        else if (ext === 'xls') mimeType = 'application/vnd.ms-excel';
        else if (ext === 'xlsx') mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      }

      newAttachments.push({
        name: uploadedData?.name || file.name,
        filename: uploadedData?.filename || file.name,
        size: uploadedData?.size || file.size,
        size_formatted: uploadedData?.size_formatted || sizeFormatted,
        type: uploadedData?.type || mimeType,
        url: uploadedData?.url || base64DataUrl,
        download_url: uploadedData?.download_url || uploadedData?.url || base64DataUrl,
        file_id: uploadedData?.file_id,
        data_url: uploadedData ? null : base64DataUrl,
      });
    }

    setSettlementForm((prev) => ({ ...prev, attachments: newAttachments }));
    setUploadingBills(false);
    setUploadProgress('');
    e.target.value = '';

    if (failureCount === 0) {
      toast.success('Supporting bill(s) attached successfully.');
    } else if (failureCount < files.length) {
      toast.warning('Some files were attached, but some failed validation.');
    } else {
      setUploadError('Failed to upload file(s). Please check format and size.');
    }
  };

  const handleRemoveAttachment = (idxToRemove) => {
    setSettlementForm((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, idx) => idx !== idxToRemove),
    }));
  };

  const handleDownloadSettlementAttachment = async (att) => {
    try {
      const rawUrl = att.download_url || att.url || (typeof att === 'string' ? att : '');
      const name = att.name || att.filename || 'Settlement_Bill';
      if (!rawUrl) {
        toast.error('Download link not available.');
        return;
      }
      if (att.is_drive_link || rawUrl.includes('drive.google.com') || rawUrl.includes('docs.google.com')) {
        window.open(rawUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      if (rawUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = rawUrl;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }
      const baseUrl = API_URL.replace('/api', '');
      const downloadEndpoint = rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
      const dlUrl = downloadEndpoint.includes('?') ? `${downloadEndpoint}&download=true` : `${downloadEndpoint}?download=true`;
      const res = await axios.get(dlUrl, { responseType: 'blob', withCredentials: true });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const bUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = bUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(bUrl);
      toast.success(`Downloaded ${name}`);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download attachment');
    }
  };

  const getAttachmentIcon = (att) => {
    const isObj = att && typeof att === 'object';
    const name = isObj ? (att.name || att.filename || '') : String(att);
    const ext = name.split('.').pop().toLowerCase();
    const isDrive = Boolean(isObj && att.is_drive_link) || name.includes('drive.google.com') || name.includes('docs.google.com');

    if (isDrive) {
      return <Link2 className="w-4 h-4 text-indigo-600" />;
    }
    if (ext === 'pdf' || (isObj && att.type?.includes('pdf'))) {
      return <FileText className="w-4 h-4 text-rose-600" />;
    }
    if (['jpg', 'jpeg', 'png', 'webp'].includes(ext) || (isObj && att.type?.startsWith('image/'))) {
      return <ImageIcon className="w-4 h-4 text-blue-600" />;
    }
    if (['xls', 'xlsx'].includes(ext) || (isObj && (att.type?.includes('excel') || att.type?.includes('spreadsheet')))) {
      return <FileSpreadsheet className="w-4 h-4 text-emerald-600" />;
    }
    return <Paperclip className="w-4 h-4 text-slate-600" />;
  };

  const getAttachmentBadge = (att) => {
    const isObj = att && typeof att === 'object';
    const name = isObj ? (att.name || att.filename || '') : String(att);
    const ext = name.split('.').pop().toLowerCase();
    const isDrive = Boolean(isObj && att.is_drive_link) || name.includes('drive.google.com') || name.includes('docs.google.com');

    if (isDrive) return 'Google Drive Link';
    if (ext === 'pdf' || (isObj && att.type?.includes('pdf'))) return 'PDF Bill';
    if (['jpg', 'jpeg', 'png'].includes(ext) || (isObj && att.type?.startsWith('image/'))) return 'Image Bill';
    if (['xls', 'xlsx'].includes(ext)) return 'Excel Sheet';
    return 'Document';
  };

  // Submit Post-Travel Expense Settlement (Employee) - Supports Draft and Final Submit
  const handleSettlementSubmit = async (e, isDraft = false) => {
    if (e) e.preventDefault();
    if (uploadingBills) {
      toast.error('Please wait for file uploads to complete before submitting.');
      return;
    }
    if (!isDraft && actualTotalCalc <= 0) {
      toast.error('Total expense amount must be greater than zero for final submission.');
      return;
    }
    try {
      setSubmittingSettlement(true);
      const payload = {
        actual_hotel: Number(settlementForm.actual_hotel) || 0,
        actual_food: Number(settlementForm.actual_food) || 0,
        actual_local_travel: Number(settlementForm.actual_local_travel) || 0,
        actual_misc: Number(settlementForm.actual_misc) || 0,
        actual_ticket: Number(settlementForm.actual_ticket) || 0,
        actual_other: Number(settlementForm.actual_other) || 0,
        employee_notes: settlementForm.employee_notes,
        attachments: settlementForm.attachments || [],
        google_drive_links: settlementForm.google_drive_links || [],
        google_drive_link: settlementForm.google_drive_links?.[0] || '',
        status: isDraft ? 'draft' : 'submitted',
        is_draft: isDraft,
      };

      const { data } = await axios.post(`${API_URL}/travel-desk/requests/${id}/submit-settlement`, payload, {
        withCredentials: true,
      });
      toast.success(data?.message || (isDraft ? 'Settlement draft saved successfully!' : 'Post-travel expense settlement submitted successfully!'));
      fetchRequestDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit expense settlement');
    } finally {
      setSubmittingSettlement(false);
    }
  };

  // Process Finance Settlement (Finance Reviewer)
  const handleFinanceSettlementSubmit = async (actionType) => {
    try {
      setProcessingFinSettlement(true);
      const payload = {
        settlement_action: actionType,
        approved_hotel: Number(finSettlementForm.approved_hotel) || 0,
        approved_food: Number(finSettlementForm.approved_food) || 0,
        approved_local_travel: Number(finSettlementForm.approved_local_travel) || 0,
        approved_misc: Number(finSettlementForm.approved_misc) || 0,
        payment_reference: finSettlementForm.payment_reference,
        finance_remarks: finSettlementForm.finance_remarks,
      };

      await axios.post(`${API_URL}/travel-desk/requests/${id}/finance-settlement`, payload, {
        withCredentials: true,
      });
      toast.success(`Expense settlement ${actionType === 'approve' ? 'settled' : 'rejected'} successfully!`);
      fetchRequestDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process settlement verification');
    } finally {
      setProcessingFinSettlement(false);
    }
  };

  // Add Daily Expense Handler
  const handleAddExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!isActualTraveller) {
      toast.error('Only the actual traveller is authorized to submit expenses and claims.');
      return;
    }
    if (!expenseForm.expense_date || !expenseForm.expense_category || !expenseForm.amount_spent) {
      toast.error('Date, Category and Amount are required.');
      return;
    }
    try {
      setSavingExpense(true);
      await axios.post(`${API_URL}/travel-desk/requests/${id}/daily-expenses`, expenseForm, { withCredentials: true });
      toast.success('Daily expense recorded successfully!');
      setShowAddExpenseModal(false);
      setExpenseForm({
        expense_date: new Date().toISOString().split('T')[0],
        expense_category: 'Food & Meals',
        description: '',
        location: '',
        amount_spent: '',
        paid_by: 'Employee',
        payment_mode: 'Cash/UPI',
        advance_ref_no: '',
        remarks: '',
        bill_attachments: [],
      });
      fetchRequestDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record expense');
    } finally {
      setSavingExpense(false);
    }
  };

  // Delete Daily Expense Handler
  const handleDeleteExpense = async (expenseId) => {
    if (!isActualTraveller) {
      toast.error('Only the actual traveller is authorized to submit expenses and claims.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this expense entry?')) return;
    try {
      await axios.delete(`${API_URL}/travel-desk/requests/${id}/daily-expenses/${expenseId}`, { withCredentials: true });
      toast.success('Expense deleted successfully!');
      fetchRequestDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete expense');
    }
  };

  // Additional Advance Request Handler
  const handleAddAdvanceSubmit = async (e) => {
    e.preventDefault();
    if (metrics && metrics.advance_utilisation_pct < 75.0 && metrics.total_advance_received > 0) {
      toast.error(`Additional advance is blocked: Current advance utilisation is ${metrics.advance_utilisation_pct}% (< 75%). Please record eligible expenses and submit bills first.`);
      return;
    }
    try {
      setSubmittingAddAdvance(true);
      await axios.post(`${API_URL}/travel-desk/requests/${id}/additional-advance`, addAdvanceForm, { withCredentials: true });
      toast.success('Additional advance requested successfully!');
      setShowAddAdvanceModal(false);
      setAddAdvanceForm({ requested_amount: '', remarks: '', hotel_amount: '', food_amount: '', local_travel_amount: '', misc_amount: '' });
      fetchRequestDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request additional advance');
    } finally {
      setSubmittingAddAdvance(false);
    }
  };

  // Advance Action (Approve / Reject) Handler
  const handleAdvanceActionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAdvanceForAction) return;
    try {
      setSubmittingAdvanceAction(true);
      await axios.post(`${API_URL}/travel-desk/requests/${id}/additional-advance/${selectedAdvanceForAction.advance_ref_no}/action`, advanceActionForm, { withCredentials: true });
      toast.success(`Advance ${selectedAdvanceForAction.advance_ref_no} ${advanceActionForm.action}d successfully!`);
      setSelectedAdvanceForAction(null);
      fetchRequestDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process advance action');
    } finally {
      setSubmittingAdvanceAction(false);
    }
  };

  // Advance Disburse Handler
  const handleAdvanceDisburseSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAdvanceForDisburse) return;
    try {
      setSubmittingAdvanceDisburse(true);
      await axios.post(`${API_URL}/travel-desk/requests/${id}/additional-advance/${selectedAdvanceForDisburse.advance_ref_no}/disburse`, advanceDisburseForm, { withCredentials: true });
      toast.success(`Advance ${selectedAdvanceForDisburse.advance_ref_no} disbursed successfully!`);
      setSelectedAdvanceForDisburse(null);
      fetchRequestDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disburse advance');
    } finally {
      setSubmittingAdvanceDisburse(false);
    }
  };

  // Travel Extension Handler
  const handleExtendSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmittingExtend(true);
      await axios.post(`${API_URL}/travel-desk/requests/${id}/extend`, extendForm, { withCredentials: true });
      toast.success('Travel extension submitted successfully!');
      setShowExtendModal(false);
      setExtendForm({ revised_end_date: '', reason: '', additional_advance_amount: '' });
      fetchRequestDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit travel extension');
    } finally {
      setSubmittingExtend(false);
    }
  };

  // Consolidated Final Claim Submission Handler (Traveller-Only)
  const handleFinalClaimSubmit = async () => {
    if (!isActualTraveller) {
      toast.error('Only the actual traveller is authorized to submit expenses and claims.');
      return;
    }
    try {
      setSubmittingFinalClaim(true);
      await axios.post(`${API_URL}/travel-desk/requests/${id}/submit-final-claim`, { remarks: 'Consolidated Final Travel Claim Submitted' }, { withCredentials: true });
      toast.success('Consolidated final travel claim submitted successfully for finance verification!');
      setShowFinalClaimModal(false);
      fetchRequestDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit final claim');
    } finally {
      setSubmittingFinalClaim(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[500px]">
        <div className="text-slate-500 font-semibold">Loading Travel Request Details...</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-8 text-center space-y-4">
        <p className="text-rose-600 font-bold">Travel request not found.</p>
        <button onClick={() => navigate('/travel/requests')} className="px-4 py-2 bg-slate-900 text-white font-bold text-xs rounded-xl">
          Back to Travel Requests
        </button>
      </div>
    );
  }

  const isReceiptEligible = ['submitted', 'pending_approval', 'approved', 'booked', 'completed', 'finance_approved', 'rejected'].includes(request.status);
  const userRole = (user?.role || '').toLowerCase();
  const isFinanceUser = ['tenant_admin', 'hr_admin', 'finance_reviewer', 'payroll_admin'].includes(userRole);

  const isOwner =
    (user?.employee_id && (request.employee_id === user.employee_id || request.beneficiary_employee_id === user.employee_id || request.requester_employee_id === user.employee_id)) ||
    (user?.id && (request.created_by === user.id || request.requester_employee_id === user.id)) ||
    (user?._id && (request.created_by === user._id || request.requester_employee_id === user._id));

  const isSentBack =
    ['sent_back', 'returned_for_correction', 'rework'].includes(request.status) ||
    request.approval_status === 'sent_back' ||
    request.normalized_status === 'sent_back';

  const isDraft = request.status === 'draft' || request.normalized_status === 'draft';
  const isEditable = ['draft', 'submitted', 'pending_approval', 'returned_for_correction', 'sent_back'].includes(request.status) || isSentBack || isDraft;
  const canEdit = isEditable && (hasPermission(user, 'travel_desk.edit_request.edit') || isOwner || userRole !== 'viewer');

  const settlementObj = request.settlement || {};
  const isSettlementDraft = settlementObj.settlement_status === 'draft';
  const isSettlementSubmitted = settlementObj.settlement_status && !['not_submitted', 'draft'].includes(settlementObj.settlement_status);

  const actualTotalCalc =
    (Number(settlementForm.actual_hotel) || 0) +
    (Number(settlementForm.actual_food) || 0) +
    (Number(settlementForm.actual_local_travel) || 0) +
    (Number(settlementForm.actual_misc) || 0) +
    (Number(settlementForm.actual_ticket) || 0) +
    (Number(settlementForm.actual_other) || 0);

  const advReceivedForCalc = request.advance_payment?.paid_amount || request.approved_advance_amount || request.advance_details?.approved_advance_amount || request.requested_advance_amount || request.advance_details?.total_advance || 0;
  const netBalanceCalc = actualTotalCalc - advReceivedForCalc;

  const isActualTraveller = Boolean(
    user && request && (
      String(user.employee_id || user.id || user._id) === String(request.beneficiary_employee_id || request.employee_id) ||
      (user.employee_code && String(user.employee_code) === String(request.beneficiary_employee_code || request.employee_code)) ||
      ['tenant_admin', 'super_admin'].includes(userRole)
    )
  );

  const metrics = travelDailyData?.dashboard || null;
  const dailyExpensesList = travelDailyData?.daily_expenses || [];
  const advancesList = travelDailyData?.advances || request.advances || [];
  const extensionsList = travelDailyData?.extensions || request.extension_history || [];
  const passengersList = travelDailyData?.passengers || request.passengers || [];

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto font-sans">
      {/* Header Bar */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/travel/requests')}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <span className="font-mono text-xs font-bold text-amber-400">{request.request_number}</span>
              <h1 className="text-xl font-black tracking-tight">{request.employee_name} — {request.purpose}</h1>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isReceiptEligible && hasPermission(user, 'travel_desk.request_detail.view') && (
            <button
              onClick={handleDownloadReceipt}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-md flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> Download PDF Dossier
            </button>
          )}

          {canEdit && (
            <button
              onClick={() => navigate(`/travel/request/${id}/edit`)}
              className={`px-4 py-2.5 font-bold text-xs rounded-xl shadow-md flex items-center gap-1.5 transition-colors ${
                isSentBack
                  ? 'bg-amber-500 hover:bg-amber-400 text-slate-950'
                  : 'bg-blue-600 hover:bg-blue-500 text-white'
              }`}
            >
              {isSentBack ? (
                <>
                  <RotateCcw className="w-4 h-4" /> Rework &amp; Resubmit Request
                </>
              ) : isDraft ? (
                <>
                  <Edit className="w-4 h-4" /> Edit &amp; Submit Draft
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4" /> Edit Request
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Rework / Sent Back Notification Banner */}
      {isSentBack && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 flex items-start justify-between gap-3 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-black text-amber-950 text-xs uppercase tracking-wide">
                Application Sent Back for Rework / Correction
              </h4>
              <p className="text-amber-900 text-xs mt-1 font-medium whitespace-pre-wrap">
                {request.return_remarks || 'This application was returned by an approver for correction. Please review the remarks, make necessary adjustments, and resubmit.'}
              </p>
              {request.sent_back_by && (
                <div className="text-[11px] text-amber-800 font-semibold mt-1">
                  Sent back by: {request.sent_back_by} {request.sent_back_at ? `on ${request.sent_back_at.slice(0, 10)}` : ''}
                </div>
              )}
            </div>
          </div>
          {canEdit && (
            <button
              onClick={() => navigate(`/travel/request/${id}/edit`)}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white font-extrabold text-xs rounded-xl shadow-sm shrink-0 flex items-center gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Open Rework Form
            </button>
          )}
        </div>
      )}

      {/* Draft Notification Banner */}
      {isDraft && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-sm">
          <div className="flex items-center gap-2.5 text-blue-900 text-xs">
            <Clock className="w-4 h-4 text-blue-600 shrink-0" />
            <span>
              This travel request is currently saved as a <b>Draft</b>. You can continue editing your travel details, route, passengers, and advance requisition until you are ready to submit.
            </span>
          </div>
          {canEdit && (
            <button
              onClick={() => navigate(`/travel/request/${id}/edit`)}
              className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-sm shrink-0 flex items-center gap-1"
            >
              <Edit className="w-3.5 h-3.5" /> Continue Editing
            </button>
          )}
        </div>
      )}

      {/* UNIFIED OUTSTATION TRAVEL MANAGEMENT: ROLES & FACILITY BADGE */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200 space-y-4" data-testid="unified-roles-facility-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-black bg-blue-50 text-blue-700 px-3 py-1 rounded-xl border border-blue-200">
              TRAVEL ID: {request.request_number}
            </span>
            <span className="text-xs font-bold bg-indigo-50 text-indigo-700 px-3 py-1 rounded-xl border border-indigo-200 capitalize">
              Facility: {(request.travel_facility_type || 'ticket_and_advance').replace(/_/g, ' ')}
            </span>
            <span className="text-xs font-bold bg-slate-100 text-slate-700 px-3 py-1 rounded-xl border border-slate-200 capitalize">
              Status: {(request.status || 'submitted').replace(/_/g, ' ')}
            </span>
          </div>
          <div className="text-xs text-slate-500 font-medium">
            Single Travel Lifecycle from Requisition to Final Financial Settlement
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Role A: Requester */}
          <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-1">
            <span className="text-[10px] uppercase tracking-wider font-extrabold text-slate-400 block">A. Requester</span>
            <div className="font-bold text-slate-900 text-sm">{request.requester_name || request.employee_name}</div>
            <div className="text-xs text-slate-500 font-mono">Code: {request.requester_code || request.employee_code || 'EMP'}</div>
            <div className="text-[11px] text-slate-500 pt-1">Initiates &amp; coordinates travel request</div>
          </div>

          {/* Role B: Main Traveller / Beneficiary */}
          <div className="p-4 rounded-xl border-2 border-blue-500 bg-blue-50/50 space-y-1 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider font-extrabold text-blue-700 block">B. Main Traveller (Beneficiary)</span>
              <span className="text-[9px] font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full">Claim Owner</span>
            </div>
            <div className="font-black text-slate-900 text-sm">{request.beneficiary_employee_name || request.employee_name}</div>
            <div className="text-xs text-slate-600 font-mono">Code: {request.beneficiary_employee_code || request.employee_code || 'EMP'}</div>
            <div className="text-[11px] text-blue-800 font-semibold pt-1">
              Owns all advances, daily expenses &amp; financial claims
            </div>
          </div>

          {/* Role C: Additional Passengers */}
          <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-1">
            <span className="text-[10px] uppercase tracking-wider font-extrabold text-slate-400 block">C. Additional Passengers</span>
            <div className="font-bold text-slate-900 text-sm">
              {passengersList.length > 1 ? `${passengersList.length - 1} Additional Passenger(s)` : 'No Additional Passengers'}
            </div>
            <div className="text-[11px] text-amber-700 font-medium">
              Ticket booking coordination only (no claim rights)
            </div>
            {passengersList.length > 1 && (
              <div className="text-[11px] text-slate-600 truncate pt-1">
                {passengersList.slice(1).map(p => p.name || p.employee_name).join(', ')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* EMPLOYEE ADVANCE & EXPENSE DASHBOARD */}
      {metrics && (
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white rounded-2xl p-6 shadow-xl space-y-5" data-testid="advance-expense-dashboard">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
            <div>
              <h3 className="text-base font-black flex items-center gap-2 text-white">
                <Wallet className="w-5 h-5 text-amber-400" /> Employee Advance &amp; Expense Dashboard
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Live financial balance, advance utilisation &amp; final claim settlement status</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-xl text-xs font-bold border ${
                metrics.advance_utilisation_pct >= 75.0
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                  : 'bg-amber-500/20 text-amber-300 border-amber-500/30'
              }`}>
                Utilisation: {metrics.advance_utilisation_pct}% {metrics.advance_utilisation_pct >= 75.0 ? '✓ (>= 75% Threshold Met)' : '(< 75% Threshold)'}
              </span>
            </div>
          </div>

          {/* 75% Utilisation Progress Bar */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs font-bold">
              <span className="text-slate-300">Advance Utilisation Progress</span>
              <span className="text-amber-400">{metrics.advance_utilisation_pct}% Recorded / 75% Threshold</span>
            </div>
            <div className="w-full bg-slate-700/60 rounded-full h-3.5 overflow-hidden p-0.5 relative">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  metrics.advance_utilisation_pct >= 75.0 ? 'bg-emerald-500' : 'bg-amber-500'
                }`}
                style={{ width: `${Math.min(100, metrics.advance_utilisation_pct)}%` }}
              />
              <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                style={{ left: '75%' }}
                title="75% Mandatory Threshold for Additional Advance"
              />
            </div>
            <div className="flex justify-between text-[10px] text-slate-400">
              <span>₹ 0</span>
              <span className="text-amber-300 font-bold">75% Mandatory Threshold Line</span>
              <span>Total Disbursed: ₹ {(metrics.total_advance_received).toLocaleString('en-IN')}</span>
            </div>
          </div>

          {/* Key Metric Tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pt-2">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <span className="text-[10px] text-slate-400 block font-semibold uppercase">Total Advance Received</span>
              <span className="font-mono font-black text-lg text-emerald-400">₹ {(metrics.total_advance_received).toLocaleString('en-IN')}</span>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <span className="text-[10px] text-slate-400 block font-semibold uppercase">Total Expenses Recorded</span>
              <span className="font-mono font-black text-lg text-blue-400">₹ {(metrics.total_expenses_recorded).toLocaleString('en-IN')}</span>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <span className="text-[10px] text-slate-400 block font-semibold uppercase">Unspent Advance Balance</span>
              <span className="font-mono font-black text-lg text-amber-400">₹ {(metrics.unspent_advance_balance).toLocaleString('en-IN')}</span>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <span className="text-[10px] text-slate-400 block font-semibold uppercase">Employee Own-Money</span>
              <span className="font-mono font-black text-lg text-purple-400">₹ {(metrics.employee_own_money_expenses).toLocaleString('en-IN')}</span>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <span className="text-[10px] text-slate-400 block font-semibold uppercase">
                {metrics.company_payable > 0 ? 'Company Payable' : 'Employee Refund'}
              </span>
              <span className={`font-mono font-black text-lg ${metrics.company_payable > 0 ? 'text-teal-400' : 'text-rose-400'}`}>
                ₹ {(metrics.company_payable > 0 ? metrics.company_payable : metrics.employee_refund).toLocaleString('en-IN')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* DAILY EXPENSE TRACKING GRID */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4" data-testid="daily-expense-grid">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Receipt className="w-4 h-4 text-blue-600" /> Daily Expense Tracking Grid
            </h3>
            <p className="text-[11px] text-slate-500">Record itemized daily expenses with running cumulative totals and remaining advance balance</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                if (!isActualTraveller) {
                  toast.error('Only the actual traveller is authorized to submit expenses and claims.');
                  return;
                }
                setShowAddExpenseModal(true);
              }}
              className="px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Add Daily Expense
            </button>
            <button
              onClick={() => {
                if (!isActualTraveller) {
                  toast.error('Only the actual traveller is authorized to submit expenses and claims.');
                  return;
                }
                setShowFinalClaimModal(true);
              }}
              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Submit Consolidated Final Claim
            </button>
          </div>
        </div>

        {dailyExpensesList.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-xs italic bg-slate-50 rounded-xl border border-dashed border-slate-200">
            No daily expenses recorded yet. Click "Add Daily Expense" to record daily travel spend.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px] border-b border-slate-200">
                <tr>
                  <th className="p-3">Date</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Description / Location</th>
                  <th className="p-3 text-right">Amount (₹)</th>
                  <th className="p-3">Paid By</th>
                  <th className="p-3">Payment Mode</th>
                  <th className="p-3">Advance Ref</th>
                  <th className="p-3 text-right">Cumulative Expense</th>
                  <th className="p-3 text-right">Advance Balance</th>
                  <th className="p-3 text-center">Receipt</th>
                  <th className="p-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dailyExpensesList.map((exp, idx) => (
                  <tr key={exp.expense_id || idx} className="hover:bg-slate-50">
                    <td className="p-3 font-mono font-medium">{exp.expense_date}</td>
                    <td className="p-3 font-semibold text-slate-800">{exp.expense_category}</td>
                    <td className="p-3 text-slate-600">
                      <div>{exp.description || '—'}</div>
                      {exp.location && <div className="text-[10px] text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> {exp.location}</div>}
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-slate-900">
                      ₹ {(Number(exp.amount_spent) || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        exp.paid_by === 'Company' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {exp.paid_by || 'Employee'}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">{exp.payment_mode || 'Cash/UPI'}</td>
                    <td className="p-3 font-mono text-slate-600">{exp.advance_ref_no || '—'}</td>
                    <td className="p-3 text-right font-mono font-bold text-blue-700">
                      ₹ {(Number(exp.cumulative_expenses) || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-amber-700">
                      ₹ {(Number(exp.remaining_advance_balance) || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-3 text-center">
                      {(exp.bill_attachments && exp.bill_attachments.length > 0) ? (
                        <button
                          onClick={() => {
                            setPreviewAttachment(exp.bill_attachments[0]);
                            setIsPreviewOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 font-bold text-[11px] underline flex items-center justify-center gap-1"
                        >
                          <Paperclip className="w-3 h-3" /> View
                        </button>
                      ) : (
                        <span className="text-slate-400 text-[10px]">No Bill</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {isActualTraveller && request.status !== 'closed' && (
                        <button
                          onClick={() => handleDeleteExpense(exp.expense_id)}
                          className="text-rose-600 hover:text-rose-800 p-1 rounded hover:bg-rose-50"
                          title="Delete expense"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* TRAVEL ADVANCES & NON-FIFO TRACKING SECTION */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4" data-testid="advances-tracking-section">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-600" /> Travel Advances &amp; Non-FIFO Tracking
            </h3>
            <p className="text-[11px] text-slate-500">Individual advance records (ADV-001, ADV-002) with distinct status, disbursement UTR, and 75% utilisation control</p>
          </div>
          <button
            onClick={() => {
              if (metrics && metrics.total_advance_received > 0 && metrics.advance_utilisation_pct < 75.0) {
                toast.error(`Additional advance is blocked: Current utilisation is ${metrics.advance_utilisation_pct}% (< 75%). Please record eligible expenses and submit bills first.`);
                return;
              }
              setShowAddAdvanceModal(true);
            }}
            className={`px-3.5 py-2 font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 transition ${
              metrics && metrics.total_advance_received > 0 && metrics.advance_utilisation_pct < 75.0
                ? 'bg-slate-300 text-slate-600 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
          >
            <Plus className="w-3.5 h-3.5" /> Request Additional Advance
          </button>
        </div>

        {advancesList.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-xs italic bg-slate-50 rounded-xl border border-dashed border-slate-200">
            No advance requested for this travel request.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px] border-b border-slate-200">
                <tr>
                  <th className="p-3">Advance Ref</th>
                  <th className="p-3 text-right">Requested Amount</th>
                  <th className="p-3 text-right">Approved Amount</th>
                  <th className="p-3 text-right">Paid / Disbursed</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Disbursement UTR &amp; Date</th>
                  <th className="p-3">Remarks</th>
                  <th className="p-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {advancesList.map((adv, idx) => (
                  <tr key={adv.advance_ref_no || idx} className="hover:bg-slate-50">
                    <td className="p-3 font-mono font-bold text-blue-700">{adv.advance_ref_no || `ADV-00${idx + 1}`}</td>
                    <td className="p-3 text-right font-mono font-bold text-slate-900">₹ {(Number(adv.requested_amount) || 0).toLocaleString('en-IN')}</td>
                    <td className="p-3 text-right font-mono font-bold text-emerald-700">
                      {adv.approved_amount !== null && adv.approved_amount !== undefined ? `₹ ${(Number(adv.approved_amount)).toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-blue-900">
                      ₹ {(Number(adv.paid_amount) || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                        ['disbursed', 'paid'].includes(adv.status) ? 'bg-emerald-100 text-emerald-800' :
                        adv.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                        adv.status === 'rejected' ? 'bg-rose-100 text-rose-800' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {adv.status || 'pending_approval'}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-slate-600">
                      {adv.payment_reference ? `${adv.payment_reference} (${adv.disbursement_date || 'N/A'})` : '—'}
                    </td>
                    <td className="p-3 text-slate-500 text-[11px]">{adv.remarks || adv.approval_remarks || '—'}</td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        {isFinanceUser && adv.status === 'approved' && (
                          <button
                            onClick={() => {
                              setSelectedAdvanceForDisburse(adv);
                              setAdvanceDisburseForm({
                                payment_reference: '',
                                paid_amount: adv.approved_amount || adv.requested_amount || '',
                                payment_date: new Date().toISOString().split('T')[0],
                              });
                            }}
                            className="px-2.5 py-1 bg-emerald-600 text-white rounded text-[11px] font-bold hover:bg-emerald-500"
                          >
                            Disburse
                          </button>
                        )}
                        {(isFinanceUser || userRole === 'manager') && adv.status === 'pending_approval' && (
                          <button
                            onClick={() => {
                              setSelectedAdvanceForAction(adv);
                              setAdvanceActionForm({
                                action: 'approve',
                                approved_amount: adv.requested_amount || '',
                                remarks: '',
                              });
                            }}
                            className="px-2.5 py-1 bg-blue-600 text-white rounded text-[11px] font-bold hover:bg-blue-500"
                          >
                            Review
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* TRAVEL EXTENSION & PASSENGER AMENDMENT SECTION */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4" data-testid="travel-extension-section">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4 text-indigo-600" /> Travel Extension &amp; Passenger Amendment
            </h3>
            <p className="text-[11px] text-slate-500">Extend travel duration or amend passenger manifest under the same Travel ID</p>
          </div>
          <button
            onClick={() => {
              setExtendForm({
                revised_end_date: request.end_date || '',
                reason: '',
                additional_advance_amount: '',
              });
              setShowExtendModal(true);
            }}
            className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5"
          >
            <Clock className="w-3.5 h-3.5" /> Extend Travel / Amend Passengers
          </button>
        </div>

        {extensionsList.length === 0 ? (
          <div className="p-4 text-xs text-slate-500 bg-slate-50 rounded-xl border border-slate-200">
            Current End Date: <span className="font-bold text-slate-900 font-mono">{request.end_date || 'N/A'}</span>. No extensions recorded.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs font-bold text-indigo-900">Extension History ({extensionsList.length})</div>
            <div className="divide-y divide-slate-100 bg-slate-50 rounded-xl border border-slate-200 p-3">
              {extensionsList.map((ext, idx) => (
                <div key={idx} className="py-2 flex items-center justify-between text-xs">
                  <div>
                    <span className="font-bold text-slate-900 font-mono">{ext.previous_end_date} → {ext.revised_end_date}</span>
                    <span className="text-slate-500 text-[11px] ml-2 font-medium">Reason: {ext.reason}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">
                    By {ext.requested_by} on {String(ext.requested_at || '').slice(0, 10)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Main Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Requisition, Advance & Settlement */}
        <div className="lg:col-span-2 space-y-6">
          {/* Employee & Travel Overview */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-blue-600" /> Employee &amp; Requisition Overview
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
              <div>
                <span className="text-slate-400 font-medium block">Employee Name</span>
                <span className="font-bold text-slate-900">{request.employee_name}</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Department</span>
                <span className="font-bold text-slate-900">{request.department_name || request.department || 'N/A'}</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Project</span>
                <span className="font-bold text-slate-900">{request.project_name ? `${request.project_name} (${request.project_code || 'N/A'})` : (request.project || 'N/A')}</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Designation / Grade</span>
                <span className="font-bold text-slate-900">{request.designation} ({request.grade})</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Travel Mode</span>
                <span className="font-bold capitalize text-blue-600">{request.travel_type}</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Category</span>
                <span className="font-bold text-slate-900">{request.travel_category || 'Domestic'}</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium block">Booking Responsibility</span>
                {request.booking_responsibility === 'self_booked' ? (
                  <span className="inline-flex items-center gap-1 font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-md text-[11px] mt-0.5" data-testid="detail-booking-responsibility">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-600"></span>
                    Self-Booked (Employee)
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md text-[11px] mt-0.5" data-testid="detail-booking-responsibility">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                    Company-Booked (Corporate)
                  </span>
                )}
              </div>
              {Number(request.estimated_budget) > 0 && (
                <div>
                  <span className="text-slate-400 font-medium block">Historical Est. Budget</span>
                  <span className="font-mono font-bold text-emerald-600">₹ {(request.estimated_budget || 0).toLocaleString('en-IN')}</span>
                </div>
              )}
            </div>

            {request.policy_warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl text-xs text-amber-900 flex items-center gap-2 font-medium">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>Policy Warnings: {request.policy_warnings.join(' | ')}</span>
              </div>
            )}
          </div>

          {/* SELF BOOKING REIMBURSEMENT & SUBMITTED ATTACHMENTS SECTION */}
          {(request.is_self_booking || request.claim_type === 'self_booking' || request.self_booking_details || (request.attachments && request.attachments.length > 0)) && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4" data-testid="self-booking-claim-section">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-purple-600" /> Reimbursement Claim &amp; Submitted Receipts
                </h3>
                <span className="font-mono font-black text-purple-700 text-sm bg-purple-50 border border-purple-200 px-3 py-1 rounded-xl">
                  Total Claim: ₹ {(request.self_booking_details?.total_claim_amount || request.total_claim_amount || request.estimated_budget || 0).toLocaleString('en-IN')}
                </span>
              </div>

              {request.self_booking_details && (() => {
                const sb = request.self_booking_details;
                const ticketAmt = sb.ticket_amount || 0;
                const hotelAmt = sb.hotel_amount || 0;
                const foodAmt = sb.food_amount || 0;
                const localAmt = sb.local_travel_amount || sb.conveyance_amount || 0;
                const miscAmt = sb.misc_amount || 0;

                const allItems = [
                  ...(sb.ticket_items || (ticketAmt > 0 ? [{ amount: ticketAmt, date: sb.ticket_date, vendor_ref: sb.ticket_vendor_ref, remarks: sb.ticket_remarks }] : [])).map(i => ({ ...i, category: 'Flight / Train / Ticket' })),
                  ...(sb.hotel_items || (hotelAmt > 0 ? [{ amount: hotelAmt, date: sb.hotel_date, vendor_ref: sb.hotel_vendor_ref, remarks: sb.hotel_remarks }] : [])).map(i => ({ ...i, category: 'Hotel & Accommodation' })),
                  ...(sb.food_items || (foodAmt > 0 ? [{ amount: foodAmt, date: sb.food_date, remarks: sb.food_remarks }] : [])).map(i => ({ ...i, category: 'Food & Meals' })),
                  ...(sb.local_travel_items || (localAmt > 0 ? [{ amount: localAmt, date: sb.local_travel_date, remarks: sb.local_travel_remarks }] : [])).map(i => ({ ...i, category: 'Local Conveyance (Cab)' })),
                  ...(sb.misc_items || (miscAmt > 0 ? [{ amount: miscAmt, date: sb.misc_date, remarks: sb.misc_remarks }] : [])).map(i => ({ ...i, category: 'Miscellaneous' }))
                ];

                return (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <div>
                        <span className="text-slate-400 font-medium block">1. Ticket / Fare</span>
                        <span className="font-mono font-bold text-slate-900">₹ {(ticketAmt).toLocaleString('en-IN')}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 font-medium block">2. Accommodation</span>
                        <span className="font-mono font-bold text-slate-900">₹ {(hotelAmt).toLocaleString('en-IN')}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 font-medium block">3. Food / Meals</span>
                        <span className="font-mono font-bold text-slate-900">₹ {(foodAmt).toLocaleString('en-IN')}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 font-medium block">4. Local Travel</span>
                        <span className="font-mono font-bold text-slate-900">₹ {(localAmt).toLocaleString('en-IN')}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 font-medium block">5. Miscellaneous</span>
                        <span className="font-mono font-bold text-slate-900">₹ {(miscAmt).toLocaleString('en-IN')}</span>
                      </div>
                    </div>

                    {allItems.length > 0 && (
                      <div className="overflow-x-auto rounded-xl border border-slate-200">
                        <table className="w-full text-left border-collapse text-xs bg-white">
                          <thead>
                            <tr className="bg-slate-100/80 text-slate-700 font-bold border-b border-slate-200 text-[11px]">
                              <th className="p-2.5">#</th>
                              <th className="p-2.5">Category</th>
                              <th className="p-2.5">Date</th>
                              <th className="p-2.5">Reference / PNR</th>
                              <th className="p-2.5">Description / Remarks</th>
                              <th className="p-2.5 text-right">Amount (₹)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {allItems.map((it, idx) => (
                              <tr key={idx} className="hover:bg-slate-50">
                                <td className="p-2.5 font-bold text-slate-400">{idx + 1}</td>
                                <td className="p-2.5 font-bold text-slate-900">{it.category}</td>
                                <td className="p-2.5 text-slate-600">{it.date || '—'}</td>
                                <td className="p-2.5 font-mono text-slate-700">{it.vendor_ref || '—'}</td>
                                <td className="p-2.5 text-slate-700">{it.remarks || '—'}</td>
                                <td className="p-2.5 font-mono font-bold text-slate-900 text-right">
                                  ₹ {Number(it.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Submitted Bills & Receipts Grid */}
              {(() => {
                const attList = (request.attachments && request.attachments.length > 0)
                  ? request.attachments
                  : (request.self_booking_details?.all_attachments || []);

                if (!attList || attList.length === 0) {
                  return (
                    <div className="p-4 bg-slate-50 rounded-xl border border-dashed border-slate-200 text-center text-xs text-slate-400 italic">
                      No bill or receipt attachments submitted.
                    </div>
                  );
                }

                return (
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                        <Paperclip className="w-3.5 h-3.5 text-blue-600" /> Submitted Bills &amp; Receipts ({attList.length})
                      </h4>
                      <span className="text-[10px] text-slate-400 font-medium">Click View to preview or Download to save</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {attList.map((att, idx) => {
                        const isObj = att && typeof att === 'object';
                        const attName = isObj ? (att.name || att.filename || `Receipt #${idx + 1}`) : (typeof att === 'string' && att.trim() ? att.split('?')[0].split('/').pop() || `Receipt #${idx + 1}` : `Receipt #${idx + 1}`);
                        const isPdf = attName.toLowerCase().endsWith('.pdf') || (isObj && att.type?.includes('pdf'));

                        return (
                          <div
                            key={idx}
                            className="p-3 bg-slate-50 hover:bg-slate-100/90 border border-slate-200 rounded-xl flex items-center justify-between gap-2 transition-all shadow-xs"
                          >
                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                              <div className={`p-2 rounded-lg shrink-0 ${isPdf ? 'bg-rose-100 text-rose-600' : 'bg-blue-100 text-blue-600'}`}>
                                {isPdf ? <FileText className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-bold text-slate-900 text-xs truncate" title={attName}>
                                  {attName}
                                </p>
                                <span className="text-[10px] text-slate-400 font-mono">
                                  {isObj && att.size_formatted ? att.size_formatted : isPdf ? 'PDF Document' : 'Receipt File'}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-1.5 shrink-0">
                              <button
                                type="button"
                                onClick={() => {
                                  setPreviewAttachment(att);
                                  setIsPreviewOpen(true);
                                }}
                                className="px-2.5 py-1.5 bg-white hover:bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold flex items-center gap-1 shadow-xs transition-colors"
                                title="View Bill"
                              >
                                <Eye className="w-3.5 h-3.5" /> View
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setPreviewAttachment(att);
                                  setIsPreviewOpen(true);
                                }}
                                className="p-1.5 bg-white hover:bg-slate-100 text-slate-600 border border-slate-200 rounded-lg transition-colors"
                                title="Download Attachment"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* TICKET BOOKING INFORMATION SECTION */}
          {(() => {
            const tb = request.ticket_booking_info || request.booking_record || {};
            const isBooked = request.ticket_booking_status === 'booked' || request.booking_status === 'confirmed' || !!request.booking_record || (tb.booking_status === 'booked');
            const isNotBooked = request.ticket_booking_status === 'not_booked' || request.booking_status === 'not_booked' || (tb.booking_status === 'not_booked') || !!request.ticket_not_booked_reason;

            if (!isBooked && !isNotBooked && !['approved', 'booked', 'completed'].includes(request.status)) {
              return null;
            }

            return (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4" data-testid="section-ticket-booking-info">
                <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                  <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                    <Plane className="w-4 h-4 text-indigo-600" /> Ticket Booking Information
                  </h3>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wide border ${
                      isBooked
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                        : isNotBooked
                        ? 'bg-amber-50 text-amber-900 border-amber-300'
                        : 'bg-slate-100 text-slate-700 border-slate-200'
                    }`}
                  >
                    {isBooked ? '✓ Ticket Booked' : isNotBooked ? '⚠ Ticket Not Booked' : 'Pending Booking'}
                  </span>
                </div>

                {isNotBooked ? (
                  <div className="space-y-3 p-4 bg-amber-50/70 border border-amber-200 rounded-xl">
                    <div className="flex items-start gap-2.5">
                      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div className="space-y-1 text-xs">
                        <span className="font-bold text-amber-950 uppercase text-[10px] tracking-wide block">
                          Reason for Not Booking Ticket:
                        </span>
                        <p className="text-amber-900 font-medium whitespace-pre-wrap leading-relaxed">
                          {request.ticket_not_booked_reason || tb.not_booked_reason || 'Ticket was not booked by booking controller / travel desk.'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-amber-200 text-xs text-amber-800">
                      <div>
                        <span className="font-bold text-amber-950">Recorded Date:</span>{' '}
                        {request.ticket_not_booked_date || tb.booking_date || request.updated_at?.slice(0, 10) || '—'}
                      </div>
                      <div>
                        <span className="font-bold text-amber-950">Recorded By:</span>{' '}
                        {request.ticket_not_booked_by || tb.booked_by || 'Booking Controller / Travel Desk'}
                      </div>
                    </div>

                    <div className="text-[11px] text-slate-500 italic pt-1">
                      Note: The employee or travel desk can enter the ticket details later if required.
                    </div>
                  </div>
                ) : isBooked ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-[10px] text-slate-400 font-bold uppercase block">PNR / Reference</span>
                        <span className="font-mono font-bold text-slate-900 text-sm">
                          {tb.pnr_reference || request.booking_record?.pnr_reference || '—'}
                        </span>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-[10px] text-slate-400 font-bold uppercase block">Carrier / Flight / Train</span>
                        <span className="font-bold text-slate-800 text-sm">
                          {tb.carrier_name_or_no || request.booking_record?.carrier_name_or_no || '—'}
                        </span>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-[10px] text-slate-400 font-bold uppercase block">Vendor / Carrier</span>
                        <span className="font-bold text-slate-800 text-sm">
                          {tb.vendor_name || request.booking_record?.vendor_name || 'Preferred Vendor'}
                        </span>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-[10px] text-slate-400 font-bold uppercase block">Payment Method</span>
                        <span className="font-bold text-indigo-700 text-sm">
                          {tb.credit_card_masked || request.booking_record?.credit_card_masked || 'Corporate Account'}
                        </span>
                      </div>
                    </div>

                    {/* Fare Breakdown */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2 text-xs">
                      <span className="font-bold text-slate-700 text-[11px] uppercase tracking-wide block">
                        Fare Breakdown
                      </span>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <span className="text-[10px] text-slate-400 block">Base Fare</span>
                          <span className="font-mono font-bold text-slate-800 text-sm">
                            ₹ {Number(tb.base_fare ?? request.booking_record?.base_fare ?? 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 block">Taxes &amp; Fees</span>
                          <span className="font-mono font-bold text-slate-800 text-sm">
                            ₹ {Number(tb.taxes ?? request.booking_record?.taxes ?? 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 block">Convenience Fee</span>
                          <span className="font-mono font-bold text-slate-800 text-sm">
                            ₹ {Number(tb.convenience_fee ?? request.booking_record?.convenience_fee ?? 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 block">Total Fare</span>
                          <span className="font-mono font-black text-emerald-700 text-base">
                            ₹ {Number(tb.total_amount ?? request.booking_record?.total_amount ?? 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-500 pt-1">
                      <div>
                        <span className="font-bold text-slate-700">Booking Date:</span>{' '}
                        {tb.booking_date || request.booking_record?.booking_date || '—'}
                      </div>
                      <div>
                        <span className="font-bold text-slate-700">Booked By:</span>{' '}
                        {tb.booked_by || request.booking_record?.booked_by || 'Travel Desk Executive'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic p-3 bg-slate-50 rounded-xl">
                    Tickets are pending booking by the assigned Booking Controller or Travel Desk Executive.
                  </div>
                )}
              </div>
            );
          })()}

          {/* TRAVEL ADVANCE REQUEST SECTION */}
          {request.advance_requested && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4" data-testid="advance-details-section">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-amber-600" /> Travel Advance Requisition
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${
                  request.advance_status === 'paid'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : request.advance_status === 'rejected'
                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                    : request.advance_status === 'payment_pending'
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  Advance: {
                    request.advance_status === 'paid'
                      ? 'Paid'
                      : request.advance_status === 'payment_pending'
                      ? 'Approved – Pending Finance Payment'
                      : request.advance_status?.replace(/_/g, ' ') || 'Pending Approval'
                  }
                </span>
              </div>

              {/* Advance Category Breakdown Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-amber-50/60 p-4 rounded-xl border border-amber-200/80">
                {request.advance_owner_name && (
                  <div className="col-span-2 sm:col-span-4 bg-white/80 p-2.5 rounded-lg border border-amber-300 flex items-center justify-between font-medium">
                    <span className="text-slate-600">Advance Account Owner:</span>
                    <span className="font-bold text-amber-950 font-mono">
                      {request.advance_owner_name} ({request.advance_owner_employee_id || request.advance_owner_code || 'N/A'})
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-slate-500 font-medium block">1. Hotel Amount</span>
                  <span className="font-mono font-bold text-slate-900">₹ {(request.advance_details?.hotel_amount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-slate-500 font-medium block">2. Food Amount</span>
                  <span className="font-mono font-bold text-slate-900">₹ {(request.advance_details?.food_amount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-slate-500 font-medium block">3. Local Travel</span>
                  <span className="font-mono font-bold text-slate-900">₹ {(request.advance_details?.local_travel_amount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-slate-500 font-medium block">4. Miscellaneous</span>
                  <span className="font-mono font-bold text-slate-900">₹ {(request.advance_details?.misc_amount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="col-span-2 sm:col-span-4 pt-2 border-t border-amber-200/80 space-y-1.5 text-amber-950 font-bold">
                  <div className="flex justify-between items-center text-slate-700">
                    <span>Requested Advance:</span>
                    <span className="font-mono text-sm">₹ {(request.requested_advance_amount ?? request.advance_details?.total_advance ?? 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between items-center text-amber-950 border-t border-amber-200/60 pt-1 text-sm">
                    <span>Approved Advance Amount:</span>
                    <span className="font-mono text-base font-black text-amber-900">
                      {request.approved_advance_amount !== null && request.approved_advance_amount !== undefined
                        ? `₹ ${Number(request.approved_advance_amount).toLocaleString('en-IN')}`
                        : request.advance_details?.approved_advance_amount !== null && request.advance_details?.approved_advance_amount !== undefined
                        ? `₹ ${Number(request.advance_details.approved_advance_amount).toLocaleString('en-IN')}`
                        : 'Pending Approval'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Advance Payment Record if processed */}
              {request.advance_payment?.payment_status && request.advance_payment.payment_status !== 'pending' && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2 text-xs">
                  <div className="font-bold text-slate-800 border-b border-slate-200 pb-1.5 flex justify-between">
                    <span>Disbursement Record</span>
                    <span className="uppercase text-emerald-700 font-mono font-black">{request.advance_payment.payment_status}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-medium text-slate-600">
                    <div>Paid Amount: <b className="text-slate-900 font-mono">₹ {(request.advance_payment.paid_amount || 0).toLocaleString('en-IN')}</b></div>
                    <div>Payment Date: <b className="text-slate-900">{request.advance_payment.payment_date || 'N/A'}</b></div>
                    <div>Ref / UTR: <b className="text-slate-900 font-mono">{request.advance_payment.payment_reference || 'N/A'}</b></div>
                    <div>Remarks: <b className="text-slate-900">{request.advance_payment.finance_remarks || 'None'}</b></div>
                  </div>
                </div>
              )}

              {/* Finance Advance Payment Action Form */}
              {isFinanceUser && request.advance_status !== 'paid' && request.status !== 'rejected' && (
                <form onSubmit={handleAdvancePaymentSubmit} className="p-4 bg-indigo-50/70 rounded-xl border border-indigo-200 space-y-3 text-xs" data-testid="form-finance-adv-payment">
                  <div className="font-bold text-indigo-900 flex items-center gap-1.5">
                    <Receipt className="w-4 h-4 text-indigo-600" /> Finance Advance Disbursement Action
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Payment Status</label>
                      <select
                        value={advPaymentForm.payment_status}
                        onChange={(e) => setAdvPaymentForm({ ...advPaymentForm, payment_status: e.target.value })}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg font-semibold"
                        data-testid="select-adv-payment-status"
                      >
                        <option value="paid">Paid / Disbursed</option>
                        <option value="processing">Payment Processing</option>
                        <option value="payment_pending">Payment Pending</option>
                        <option value="rejected">Reject Advance</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Paid Amount (₹)</label>
                      <input
                        type="number"
                        value={advPaymentForm.paid_amount}
                        onChange={(e) => setAdvPaymentForm({ ...advPaymentForm, paid_amount: e.target.value })}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg font-mono font-bold"
                        data-testid="input-adv-paid-amount"
                      />
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Payment Date</label>
                      <input
                        type="date"
                        value={advPaymentForm.payment_date}
                        onChange={(e) => setAdvPaymentForm({ ...advPaymentForm, payment_date: e.target.value })}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg font-semibold"
                        data-testid="input-adv-payment-date"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Payment Ref / Transaction UTR</label>
                      <input
                        type="text"
                        placeholder="e.g. UTR9832749823"
                        value={advPaymentForm.payment_reference}
                        onChange={(e) => setAdvPaymentForm({ ...advPaymentForm, payment_reference: e.target.value })}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg font-mono"
                        data-testid="input-adv-payment-ref"
                      />
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Finance Remarks</label>
                      <input
                        type="text"
                        placeholder="Add finance remarks..."
                        value={advPaymentForm.finance_remarks}
                        onChange={(e) => setAdvPaymentForm({ ...advPaymentForm, finance_remarks: e.target.value })}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg font-medium"
                        data-testid="input-adv-finance-remarks"
                      />
                    </div>
                  </div>

                  <div className="pt-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={processingAdvPayment}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5"
                      data-testid="btn-submit-adv-payment"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {processingAdvPayment ? 'Updating...' : 'Update Advance Payment Status'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* POST-TRAVEL EXPENSE SETTLEMENT SECTION */}
          {['approved', 'booked', 'completed'].includes(request.status) && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4" data-testid="post-travel-settlement-section">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-emerald-600" /> Post-Travel Expense Settlement
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${
                  settlementObj.settlement_status === 'settled'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : settlementObj.settlement_status === 'pending_finance_settlement'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : isSettlementDraft
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-slate-100 text-slate-600 border-slate-200'
                }`}>
                  Settlement: {isSettlementDraft ? 'Draft Saved (In Progress - Editable)' : (settlementObj.settlement_status?.replace(/_/g, ' ') || 'Not Submitted')}
                </span>
              </div>

              {/* Employee Settlement Submission Form if not submitted */}
              {!isSettlementSubmitted && (
                <form onSubmit={handleSettlementSubmit} className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-4 text-xs" data-testid="form-employee-settlement">
                  <p className="text-slate-600 font-medium">
                    Submit your actual incurred expenses against your approved advance for finance verification and final settlement.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Actual Hotel Expense (₹)</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={settlementForm.actual_hotel}
                        onChange={(e) => setSettlementForm({ ...settlementForm, actual_hotel: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-mono font-bold"
                        data-testid="input-actual-hotel"
                      />
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Actual Food Expense (₹)</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={settlementForm.actual_food}
                        onChange={(e) => setSettlementForm({ ...settlementForm, actual_food: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-mono font-bold"
                        data-testid="input-actual-food"
                      />
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Actual Local Travel (₹)</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={settlementForm.actual_local_travel}
                        onChange={(e) => setSettlementForm({ ...settlementForm, actual_local_travel: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-mono font-bold"
                        data-testid="input-actual-local"
                      />
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Actual Miscellaneous (₹)</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={settlementForm.actual_misc}
                        onChange={(e) => setSettlementForm({ ...settlementForm, actual_misc: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-mono font-bold"
                        data-testid="input-actual-misc"
                      />
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Travel Tickets / Fare (₹)</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={settlementForm.actual_ticket}
                        onChange={(e) => setSettlementForm({ ...settlementForm, actual_ticket: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-mono font-bold"
                        data-testid="input-actual-ticket"
                      />
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Other Approved Expenses (₹)</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={settlementForm.actual_other}
                        onChange={(e) => setSettlementForm({ ...settlementForm, actual_other: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-mono font-bold"
                        data-testid="input-actual-other"
                      />
                    </div>
                  </div>

                  {/* Calculation Card */}
                  <div className="p-4 bg-emerald-50/80 rounded-xl border border-emerald-200 grid grid-cols-1 sm:grid-cols-4 gap-3 text-emerald-950 font-bold">
                    <div>
                      <span className="text-[10px] text-emerald-700 uppercase block">Advance Received</span>
                      <span className="font-mono text-sm">₹ {advReceivedForCalc.toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-emerald-700 uppercase block">Actual Expense Total</span>
                      <span className="font-mono text-sm">₹ {actualTotalCalc.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="sm:col-span-2 text-right">
                      <span className="text-[10px] text-emerald-700 uppercase block">
                        {netBalanceCalc > 0 ? 'Additional Reimbursement Due' : netBalanceCalc < 0 ? 'Balance to Return to Company' : 'Fully Balanced'}
                      </span>
                      <span className={`font-mono text-base ${netBalanceCalc > 0 ? 'text-emerald-700' : netBalanceCalc < 0 ? 'text-amber-800' : 'text-slate-700'}`}>
                        ₹ {Math.abs(netBalanceCalc).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>

                  {/* SUPPORTING DOCUMENTS & EXPENSE BILLS UPLOAD */}
                  <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-3" data-testid="section-settlement-bills-upload">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <label className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                          <Paperclip className="w-3.5 h-3.5 text-blue-600" /> Supporting Documents &amp; Expense Bills
                        </label>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Upload supporting hotel bills, food/meal receipts, conveyance/cab bills, and miscellaneous expense vouchers.
                        </p>
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono font-semibold bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                        PDF, JPG, JPEG, PNG, XLS, XLSX (Max 20 MB/file)
                      </span>
                    </div>

                    {/* Drag and Drop / Select Files Zone */}
                    <div className="border-2 border-dashed border-slate-300 hover:border-blue-400 bg-slate-50/70 hover:bg-blue-50/30 rounded-xl p-4 transition-all text-center">
                      <label className="cursor-pointer flex flex-col items-center justify-center gap-1.5">
                        {uploadingBills ? (
                          <div className="flex items-center gap-2 text-blue-600 font-bold text-xs py-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{uploadProgress || 'Uploading documents...'}</span>
                          </div>
                        ) : (
                          <>
                            <div className="p-2 bg-blue-100 text-blue-700 rounded-full mb-0.5">
                              <UploadCloud className="w-5 h-5" />
                            </div>
                            <span className="font-bold text-blue-700 text-xs hover:underline">
                              Click to browse and upload bills (or drag &amp; drop files here)
                            </span>
                            <span className="text-[10px] text-slate-400">
                              Multiple file selection supported • Automatically saved to secure GridFS storage
                            </span>
                          </>
                        )}
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,application/pdf,image/jpeg,image/png,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          onChange={handleFileUpload}
                          disabled={uploadingBills}
                          className="hidden"
                          data-testid="input-settlement-files"
                        />
                      </label>
                    </div>

                    {uploadError && (
                      <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>{uploadError}</span>
                      </div>
                    )}

                    {/* List of Uploaded Settlement Attachments */}
                    {settlementForm.attachments?.length > 0 && (
                      <div className="space-y-2 pt-1">
                        <span className="font-bold text-slate-700 text-[11px] flex items-center gap-1.5">
                          Attached Bills &amp; Receipts ({settlementForm.attachments.length}):
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {settlementForm.attachments.map((att, idx) => {
                            const isObj = att && typeof att === 'object';
                            const attName = isObj ? (att.name || att.filename || `Bill #${idx + 1}`) : String(att);
                            const badge = getAttachmentBadge(att);

                            return (
                              <div
                                key={idx}
                                className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl gap-2 hover:bg-slate-100/90 transition-colors shadow-xs"
                                data-testid={`attachment-item-${idx}`}
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <div className="p-1.5 bg-white rounded-lg border border-slate-200 shrink-0">
                                    {getAttachmentIcon(att)}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="font-bold text-slate-800 truncate text-xs" title={attName}>
                                      {attName}
                                    </p>
                                    <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono">
                                      <span>{badge}</span>
                                      {isObj && att.size_formatted && (
                                        <>
                                          <span>•</span>
                                          <span>{att.size_formatted}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center gap-1 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPreviewAttachment(att);
                                      setIsPreviewOpen(true);
                                    }}
                                    className="px-2 py-1 bg-white hover:bg-blue-50 text-blue-600 border border-blue-200 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors"
                                    title="Preview Document"
                                  >
                                    <Eye className="w-3.5 h-3.5" /> View
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadSettlementAttachment(att)}
                                    className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-200"
                                    title="Download Document"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAttachment(idx)}
                                    className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                                    title="Remove attachment"
                                    data-testid={`btn-remove-attachment-${idx}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* GOOGLE DRIVE LINK INTEGRATION */}
                  <div className="p-4 bg-indigo-50/40 rounded-xl border border-indigo-100 space-y-3" data-testid="section-google-drive-link">
                    <div>
                      <label className="font-bold text-indigo-950 text-xs flex items-center gap-1.5">
                        <Link2 className="w-3.5 h-3.5 text-indigo-600" /> Google Drive Supporting Documents Link (Optional)
                      </label>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Paste shareable Google Drive folder or file link containing supporting bills and expense documents.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <input
                          type="url"
                          placeholder="https://drive.google.com/drive/folders/... or https://drive.google.com/file/d/..."
                          value={driveLinkInput}
                          onChange={(e) => setDriveLinkInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddDriveLink();
                            }
                          }}
                          className="w-full h-9 pl-3 pr-8 bg-white border border-slate-300 rounded-xl text-xs font-mono outline-none focus:border-indigo-500"
                          data-testid="input-google-drive-link"
                        />
                        {driveLinkInput && (
                          <button
                            type="button"
                            onClick={() => setDriveLinkInput('')}
                            className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleAddDriveLink}
                        className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-xs flex items-center gap-1.5 shrink-0"
                        data-testid="btn-add-drive-link"
                      >
                        <Plus className="w-3.5 h-3.5" /> Attach Link
                      </button>
                    </div>

                    {settlementForm.google_drive_links?.length > 0 && (
                      <div className="space-y-1.5 pt-1">
                        <span className="text-[11px] font-bold text-indigo-900 block">Attached Google Drive Links:</span>
                        <div className="space-y-1.5">
                          {settlementForm.google_drive_links.map((link, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-2 bg-white rounded-lg border border-indigo-200 text-xs gap-2"
                              data-testid={`drive-link-item-${idx}`}
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <Link2 className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                                <a
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-indigo-700 hover:text-indigo-900 font-mono text-[11px] truncate hover:underline"
                                  title={link}
                                >
                                  {link}
                                </a>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <a
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-2 py-0.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded text-[11px] font-bold flex items-center gap-1 border border-indigo-200"
                                >
                                  <ExternalLink className="w-3 h-3" /> Test Link
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveDriveLink(idx)}
                                  className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                                  title="Remove Link"
                                  data-testid={`btn-remove-drive-link-${idx}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Employee Settlement Notes</label>
                    <textarea
                      rows={2}
                      placeholder="Add summary notes regarding trip expenses..."
                      value={settlementForm.employee_notes}
                      onChange={(e) => setSettlementForm({ ...settlementForm, employee_notes: e.target.value })}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-medium outline-none"
                      data-testid="textarea-settlement-notes"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={(e) => handleSettlementSubmit(e, true)}
                      disabled={submittingSettlement || uploadingBills}
                      className="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold text-xs rounded-xl flex items-center gap-1.5 cursor-pointer shadow-xs"
                      data-testid="btn-save-settlement-draft"
                    >
                      <Save className="w-4 h-4 text-slate-600" /> Save Settlement Draft
                    </button>
                    <button
                      type="submit"
                      disabled={submittingSettlement || uploadingBills || actualTotalCalc <= 0}
                      className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow-md flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                      data-testid="btn-submit-settlement"
                    >
                      <Send className="w-4 h-4" /> {submittingSettlement ? 'Submitting...' : uploadingBills ? 'Uploading bills...' : 'Submit Post-Travel Expense Settlement'}
                    </button>
                  </div>
                </form>
              )}

              {/* Submitted / Settled View Table */}
              {isSettlementSubmitted && (
                <div className="space-y-4" data-testid="settlement-submitted-view">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-100 font-bold uppercase text-[10px] text-slate-600 border-y border-slate-200">
                        <tr>
                          <th className="p-2.5">Category</th>
                          <th className="p-2.5">Actual Incurred</th>
                          <th className="p-2.5">Approved Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 font-medium">
                        <tr>
                          <td className="p-2.5 font-bold">1. Hotel / Accommodation</td>
                          <td className="p-2.5 font-mono">₹ {(settlementObj.actual_hotel || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2.5 font-mono font-bold text-emerald-700">
                            ₹ {(settlementObj.approved_hotel ?? settlementObj.actual_hotel ?? 0).toLocaleString('en-IN')}
                          </td>
                        </tr>
                        <tr>
                          <td className="p-2.5 font-bold">2. Food / Meals</td>
                          <td className="p-2.5 font-mono">₹ {(settlementObj.actual_food || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2.5 font-mono font-bold text-emerald-700">
                            ₹ {(settlementObj.approved_food ?? settlementObj.actual_food ?? 0).toLocaleString('en-IN')}
                          </td>
                        </tr>
                        <tr>
                          <td className="p-2.5 font-bold">3. Local Travel / Conveyance</td>
                          <td className="p-2.5 font-mono">₹ {(settlementObj.actual_local_travel || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2.5 font-mono font-bold text-emerald-700">
                            ₹ {(settlementObj.approved_local_travel ?? settlementObj.actual_local_travel ?? 0).toLocaleString('en-IN')}
                          </td>
                        </tr>
                        <tr>
                          <td className="p-2.5 font-bold">4. Miscellaneous</td>
                          <td className="p-2.5 font-mono">₹ {(settlementObj.actual_misc || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2.5 font-mono font-bold text-emerald-700">
                            ₹ {(settlementObj.approved_misc ?? settlementObj.actual_misc ?? 0).toLocaleString('en-IN')}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Summary Comparison Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                    <div>
                      <span className="text-slate-400 font-medium block">Advance Received</span>
                      <span className="font-mono font-bold text-slate-900">₹ {(settlementObj.advance_received || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 font-medium block">Actual Expense Total</span>
                      <span className="font-mono font-bold text-slate-900">₹ {(settlementObj.actual_total || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 font-medium block">Eligible Reimbursement</span>
                      <span className="font-mono font-bold text-emerald-700">
                        ₹ {(settlementObj.approved_total ?? settlementObj.actual_total ?? 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 font-medium block">Net Balance</span>
                      <span className="font-mono font-bold text-indigo-700">
                        ₹ {(settlementObj.net_balance ?? settlementObj.balance_amount ?? 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>

                  {/* EMPLOYEE SETTLEMENT NOTES */}
                  {settlementObj.employee_notes && (
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-1" data-testid="settlement-employee-notes">
                      <span className="text-slate-500 font-bold uppercase text-[10px] block">Employee Settlement Notes</span>
                      <p className="text-slate-800 whitespace-pre-wrap font-medium">{settlementObj.employee_notes}</p>
                    </div>
                  )}

                  {/* SUBMITTED SUPPORTING DOCUMENTS & BILLS */}
                  <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-3" data-testid="settlement-submitted-documents">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <h4 className="font-extrabold text-slate-800 text-xs flex items-center gap-1.5">
                        <Paperclip className="w-3.5 h-3.5 text-blue-600" /> Supporting Documents &amp; Expense Bills
                        <span className="font-mono text-slate-500 text-[11px] font-semibold">
                          ({(settlementObj.attachments || []).length} {settlementObj.attachments?.length === 1 ? 'file' : 'files'})
                        </span>
                      </h4>
                      <span className="text-[10px] text-slate-400">Click View to preview or Download to save</span>
                    </div>

                    {(!settlementObj.attachments || settlementObj.attachments.length === 0) ? (
                      <p className="text-slate-400 text-xs italic py-1">No file attachments uploaded with this settlement.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {settlementObj.attachments.map((att, idx) => {
                          const isObj = att && typeof att === 'object';
                          const attName = isObj ? (att.name || att.filename || `Bill #${idx + 1}`) : String(att);
                          const badge = getAttachmentBadge(att);

                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl gap-2 hover:bg-slate-100/90 transition-colors shadow-xs"
                              data-testid={`submitted-attachment-${idx}`}
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <div className="p-1.5 bg-white rounded-lg border border-slate-200 shrink-0">
                                  {getAttachmentIcon(att)}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="font-bold text-slate-800 truncate text-xs" title={attName}>
                                    {attName}
                                  </p>
                                  <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono">
                                    <span>{badge}</span>
                                    {isObj && att.size_formatted && (
                                      <>
                                        <span>•</span>
                                        <span>{att.size_formatted}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPreviewAttachment(att);
                                    setIsPreviewOpen(true);
                                  }}
                                  className="px-2.5 py-1 bg-white hover:bg-blue-50 text-blue-600 border border-blue-200 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors shadow-xs"
                                  title="View Document"
                                  data-testid={`btn-view-submitted-att-${idx}`}
                                >
                                  <Eye className="w-3.5 h-3.5" /> View
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDownloadSettlementAttachment(att)}
                                  className="p-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg transition-colors shadow-xs"
                                  title="Download Document"
                                  data-testid={`btn-download-submitted-att-${idx}`}
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* SUBMITTED GOOGLE DRIVE LINKS (if any) */}
                  {(() => {
                    const driveLinks = settlementObj.google_drive_links?.length > 0
                      ? settlementObj.google_drive_links
                      : (settlementObj.google_drive_link ? [settlementObj.google_drive_link] : []);

                    if (!driveLinks || driveLinks.length === 0) return null;

                    return (
                      <div className="p-4 bg-indigo-50/40 rounded-xl border border-indigo-100 space-y-2.5 text-xs" data-testid="settlement-submitted-drive-links">
                        <div className="flex items-center justify-between border-b border-indigo-100 pb-1.5">
                          <span className="font-extrabold text-indigo-950 flex items-center gap-1.5">
                            <Link2 className="w-3.5 h-3.5 text-indigo-600" /> Google Drive Supporting Documents ({driveLinks.length})
                          </span>
                          <span className="text-[10px] text-indigo-600 font-medium">Click to open shareable drive links</span>
                        </div>
                        <div className="space-y-1.5">
                          {driveLinks.map((link, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-indigo-200 gap-2 shadow-xs">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <Link2 className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                                <span className="font-mono text-indigo-900 text-xs truncate" title={link}>
                                  {link}
                                </span>
                              </div>
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-xs flex items-center gap-1 shrink-0 transition-colors shadow-xs"
                                data-testid={`btn-open-drive-link-${idx}`}
                              >
                                <ExternalLink className="w-3.5 h-3.5" /> Open Drive
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Finance Settlement Verification Box */}
                  {isFinanceUser && settlementObj.settlement_status === 'pending_finance_settlement' && (
                    <div className="p-4 bg-emerald-50/80 rounded-xl border border-emerald-200 space-y-3 text-xs" data-testid="form-finance-settlement">
                      <div className="font-bold text-emerald-950 flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Finance Settlement Verification
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <label className="block font-bold text-slate-700 mb-1">Approved Hotel (₹)</label>
                          <input
                            type="number"
                            value={finSettlementForm.approved_hotel}
                            onChange={(e) => setFinSettlementForm({ ...finSettlementForm, approved_hotel: e.target.value })}
                            className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg font-mono font-bold"
                          />
                        </div>

                        <div>
                          <label className="block font-bold text-slate-700 mb-1">Approved Food (₹)</label>
                          <input
                            type="number"
                            value={finSettlementForm.approved_food}
                            onChange={(e) => setFinSettlementForm({ ...finSettlementForm, approved_food: e.target.value })}
                            className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg font-mono font-bold"
                          />
                        </div>

                        <div>
                          <label className="block font-bold text-slate-700 mb-1">Approved Local (₹)</label>
                          <input
                            type="number"
                            value={finSettlementForm.approved_local_travel}
                            onChange={(e) => setFinSettlementForm({ ...finSettlementForm, approved_local_travel: e.target.value })}
                            className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg font-mono font-bold"
                          />
                        </div>

                        <div>
                          <label className="block font-bold text-slate-700 mb-1">Approved Misc (₹)</label>
                          <input
                            type="number"
                            value={finSettlementForm.approved_misc}
                            onChange={(e) => setFinSettlementForm({ ...finSettlementForm, approved_misc: e.target.value })}
                            className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg font-mono font-bold"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block font-bold text-slate-700 mb-1">Payment Reference / Voucher No</label>
                          <input
                            type="text"
                            placeholder="e.g. SETTLE-893427"
                            value={finSettlementForm.payment_reference}
                            onChange={(e) => setFinSettlementForm({ ...finSettlementForm, payment_reference: e.target.value })}
                            className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg font-mono"
                          />
                        </div>

                        <div>
                          <label className="block font-bold text-slate-700 mb-1">Finance Remarks</label>
                          <input
                            type="text"
                            placeholder="Verified and settled..."
                            value={finSettlementForm.finance_remarks}
                            onChange={(e) => setFinSettlementForm({ ...finSettlementForm, finance_remarks: e.target.value })}
                            className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg font-medium"
                          />
                        </div>
                      </div>

                      <div className="pt-2 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleFinanceSettlementSubmit('reject')}
                          disabled={processingFinSettlement}
                          className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs rounded-xl"
                        >
                          Reject Settlement
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFinanceSettlementSubmit('approve')}
                          disabled={processingFinSettlement}
                          className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow-md flex items-center gap-1.5"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          {processingFinSettlement ? 'Verifying...' : 'Approve & Settle Expenses'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Journey Legs */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-3 flex items-center gap-2">
              <Plane className="w-4 h-4 text-blue-600" /> Route &amp; Journey Manifest
            </h3>

            <div className="space-y-3">
              {(request.journey_details || []).map((leg, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2 text-xs">
                  <div className="flex items-center justify-between font-bold border-b border-slate-200 pb-1.5">
                    <span className="text-blue-900 font-mono text-[11px] uppercase">Segment {idx + 1}</span>
                    <span className="capitalize text-slate-700 font-semibold bg-white px-2 py-0.5 rounded border border-slate-200">
                      {leg.travel_mode || request.travel_type} {leg.booking_preference ? `• ${leg.booking_preference}` : ''} {leg.class_entitlement ? `(${leg.class_entitlement})` : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <span className="text-slate-400 font-medium block">From</span>
                      <span className="font-bold text-slate-900">{leg.from_city}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 font-medium block">To</span>
                      <span className="font-bold text-slate-900">{leg.to_city}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 font-medium block">Departure Date &amp; Time</span>
                      <span className="font-bold text-slate-900">{leg.departure_date} {leg.preferred_time ? `(${leg.preferred_time})` : ''}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 font-medium block">Return Date</span>
                      <span className="font-bold text-slate-900">{leg.return_date || 'One Way'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Passengers */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" /> Passenger Details
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 font-bold uppercase text-[10px] text-slate-600">
                  <tr>
                    <th className="p-2.5">Passenger Name</th>
                    <th className="p-2.5">Employee ID / Type</th>
                    <th className="p-2.5">Age</th>
                    <th className="p-2.5">Gender</th>
                    <th className="p-2.5">ID Number</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium">
                  {(request.passengers || []).map((p, i) => (
                    <tr key={i}>
                      <td className="p-2.5 font-bold">{p.name}</td>
                      <td className="p-2.5 font-mono text-slate-700">
                        {p.passenger_employee_id || p.employee_code || (p.is_guest ? <span className="text-slate-400 italic">Guest</span> : <span className="text-indigo-700 font-semibold">Self</span>)}
                      </td>
                      <td className="p-2.5">{p.age}</td>
                      <td className="p-2.5">{p.gender}</td>
                      <td className="p-2.5 font-mono">{p.id_number || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column: Workflow & Timeline */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-3 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" /> APPROVAL HISTORY &amp; TIMELINE
              </span>
              {request.approved_by_name && (
                <span className="text-[10px] bg-emerald-50 text-emerald-800 font-bold px-2 py-0.5 rounded border border-emerald-200">
                  Approved
                </span>
              )}
            </h3>

            <div className="space-y-3">
              {((request.approval_history && request.approval_history.length > 0)
                ? request.approval_history
                : (request.approval_timeline && request.approval_timeline.length > 0
                    ? request.approval_timeline
                    : (request.approvals || []))
              ).map((evt, idx) => {
                const actName = evt.approver_name || evt.actor_name || evt.performed_by || 'Approver';
                const actRole = evt.role || evt.level_name || evt.actor_role || evt.step || `Level ${idx + 1}`;
                const actSt = String(evt.action || evt.status || 'PENDING').toUpperCase();
                const rawTime = evt.approved_at || evt.decided_at || evt.timestamp;
                const timeStr = rawTime ? String(rawTime).replace('T', ' ').slice(0, 19) : null;
                const comms = evt.comments || evt.remarks;

                return (
                  <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5 text-xs">
                    <div className="flex justify-between items-center font-bold text-slate-800">
                      <span className="capitalize text-slate-900">{idx + 1}. {actRole}</span>
                      <span className={`text-[10px] font-mono font-black uppercase px-2 py-0.5 rounded border ${
                        actSt === 'APPROVED'
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                          : actSt === 'REJECTED'
                          ? 'bg-rose-100 text-rose-800 border-rose-300'
                          : 'bg-blue-100 text-blue-800 border-blue-300'
                      }`}>
                        {actSt}
                      </span>
                    </div>
                    <div className="text-blue-950 font-bold text-[11px]">
                      Approved By: {actName}
                    </div>
                    {timeStr && (
                      <div className="text-slate-500 font-mono text-[10px]">
                        Date: {timeStr}
                      </div>
                    )}
                    {comms && <div className="text-slate-600 italic text-[11px] pt-1 border-t border-slate-200">"{comms}"</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL: ADD DAILY EXPENSE */}
      {showAddExpenseModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-black text-slate-900 text-base flex items-center gap-2">
                <Receipt className="w-5 h-5 text-blue-600" /> Record Daily Expense
              </h3>
              <button onClick={() => setShowAddExpenseModal(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddExpenseSubmit} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Expense Date *</label>
                  <input
                    type="date"
                    required
                    value={expenseForm.expense_date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Category *</label>
                  <select
                    value={expenseForm.expense_category}
                    onChange={(e) => setExpenseForm({ ...expenseForm, expense_category: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-semibold"
                  >
                    <option value="Food & Meals">Food &amp; Meals</option>
                    <option value="Hotel & Accommodation">Hotel &amp; Accommodation</option>
                    <option value="Local Conveyance">Local Conveyance (Cab/Auto)</option>
                    <option value="Flight / Train / Bus">Flight / Train / Bus Ticket</option>
                    <option value="Fuel / Toll / Parking">Fuel / Toll / Parking</option>
                    <option value="Client Entertainment">Client Entertainment</option>
                    <option value="Miscellaneous">Miscellaneous</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Amount Spent (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    placeholder="0.00"
                    value={expenseForm.amount_spent}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount_spent: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Location</label>
                  <input
                    type="text"
                    placeholder="e.g. Mumbai / Airport"
                    value={expenseForm.location}
                    onChange={(e) => setExpenseForm({ ...expenseForm, location: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Paid By</label>
                  <select
                    value={expenseForm.paid_by}
                    onChange={(e) => setExpenseForm({ ...expenseForm, paid_by: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                  >
                    <option value="Employee">Employee (Reimbursable / Advance)</option>
                    <option value="Company">Company (Corporate Card / Direct)</option>
                  </select>
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Payment Mode</label>
                  <select
                    value={expenseForm.payment_mode}
                    onChange={(e) => setExpenseForm({ ...expenseForm, payment_mode: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                  >
                    <option value="Cash/UPI">Cash / UPI</option>
                    <option value="Credit Card">Personal Credit Card</option>
                    <option value="Corporate Card">Corporate Card</option>
                    <option value="Net Banking">Net Banking</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Description / Bill Remarks</label>
                <input
                  type="text"
                  placeholder="e.g. Lunch with client at hotel"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Bill / Receipt Attachment</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (uploadEvt) => {
                        setExpenseForm({
                          ...expenseForm,
                          bill_attachments: [{
                            name: file.name,
                            size: file.size,
                            type: file.type,
                            data_url: uploadEvt.target?.result,
                          }]
                        });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddExpenseModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingExpense}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-xs shadow-md"
                >
                  {savingExpense ? 'Saving...' : 'Record Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: REQUEST ADDITIONAL ADVANCE (75% UTILISATION CHECK) */}
      {showAddAdvanceModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-black text-slate-900 text-base flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" /> Request Additional Advance
              </h3>
              <button onClick={() => setShowAddAdvanceModal(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* 75% Utilisation Alert */}
            {metrics && metrics.total_advance_received > 0 && metrics.advance_utilisation_pct < 75.0 ? (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-2">
                <div className="font-black text-rose-800 text-xs flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 text-rose-600" /> Mandatory 75% Utilisation Rule Blocked
                </div>
                <p className="text-xs text-rose-700 leading-relaxed">
                  Your current advance utilisation is <strong>{metrics.advance_utilisation_pct}%</strong> (Recorded: ₹{metrics.total_expenses_recorded.toLocaleString('en-IN')} of Disbursed: ₹{metrics.total_advance_received.toLocaleString('en-IN')}).
                  Company policy strictly requires at least <strong>75% utilisation</strong> with supporting bills before an additional advance can be requested.
                </p>
                <button
                  type="button"
                  onClick={() => setShowAddAdvanceModal(false)}
                  className="w-full mt-2 py-2 bg-slate-900 text-white font-bold rounded-xl text-xs"
                >
                  Close &amp; Record Daily Expenses
                </button>
              </div>
            ) : (
              <form onSubmit={handleAddAdvanceSubmit} className="space-y-4 text-xs">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800 font-medium">
                  ✓ 75% Advance Utilisation threshold satisfied ({metrics?.advance_utilisation_pct || 0}% recorded). You are eligible to request an additional advance.
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Additional Amount Required (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    required
                    placeholder="e.g. 5000"
                    value={addAdvanceForm.requested_amount}
                    onChange={(e) => setAddAdvanceForm({ ...addAdvanceForm, requested_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Hotel (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={addAdvanceForm.hotel_amount}
                      onChange={(e) => setAddAdvanceForm({ ...addAdvanceForm, hotel_amount: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Food (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={addAdvanceForm.food_amount}
                      onChange={(e) => setAddAdvanceForm({ ...addAdvanceForm, food_amount: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Purpose / Justification *</label>
                  <textarea
                    required
                    rows="3"
                    placeholder="Explain why additional advance is needed..."
                    value={addAdvanceForm.remarks}
                    onChange={(e) => setAddAdvanceForm({ ...addAdvanceForm, remarks: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowAddAdvanceModal(false)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingAddAdvance}
                    className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md"
                  >
                    {submittingAddAdvance ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* MODAL: ADVANCE REVIEW / ACTION (APPROVE / REJECT) */}
      {selectedAdvanceForAction && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-black text-slate-900 text-base">
                Review Advance {selectedAdvanceForAction.advance_ref_no}
              </h3>
              <button onClick={() => setSelectedAdvanceForAction(null)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAdvanceActionSubmit} className="space-y-4 text-xs">
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div className="text-slate-500 font-medium">Requested Amount:</div>
                <div className="font-mono font-bold text-slate-900 text-sm">
                  ₹ {(Number(selectedAdvanceForAction.requested_amount) || 0).toLocaleString('en-IN')}
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-800">
                  <input
                    type="radio"
                    name="advAction"
                    value="approve"
                    checked={advanceActionForm.action === 'approve'}
                    onChange={() => setAdvanceActionForm({ ...advanceActionForm, action: 'approve' })}
                  />
                  Approve
                </label>
                <label className="flex items-center gap-2 cursor-pointer font-bold text-rose-700">
                  <input
                    type="radio"
                    name="advAction"
                    value="reject"
                    checked={advanceActionForm.action === 'reject'}
                    onChange={() => setAdvanceActionForm({ ...advanceActionForm, action: 'reject' })}
                  />
                  Reject
                </label>
              </div>

              {advanceActionForm.action === 'approve' && (
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Approved Amount (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    required
                    value={advanceActionForm.approved_amount}
                    onChange={(e) => setAdvanceActionForm({ ...advanceActionForm, approved_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                  />
                  {Number(advanceActionForm.approved_amount) < Number(selectedAdvanceForAction.requested_amount) && (
                    <div className="text-amber-700 font-bold text-[11px] mt-1">
                      ⚠️ Approved amount is reduced. Remarks are mandatory below.
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Approver Remarks {advanceActionForm.action === 'reject' || Number(advanceActionForm.approved_amount) < Number(selectedAdvanceForAction.requested_amount) ? '*' : ''}
                </label>
                <textarea
                  required={advanceActionForm.action === 'reject' || Number(advanceActionForm.approved_amount) < Number(selectedAdvanceForAction.requested_amount)}
                  rows="3"
                  placeholder="Enter decision comments..."
                  value={advanceActionForm.remarks}
                  onChange={(e) => setAdvanceActionForm({ ...advanceActionForm, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setSelectedAdvanceForAction(null)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingAdvanceAction}
                  className={`px-5 py-2 font-bold rounded-xl text-xs shadow-md text-white ${
                    advanceActionForm.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-rose-600 hover:bg-rose-500'
                  }`}
                >
                  {submittingAdvanceAction ? 'Processing...' : advanceActionForm.action === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADVANCE DISBURSEMENT (FINANCE) */}
      {selectedAdvanceForDisburse && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-black text-slate-900 text-base">
                Disburse Advance {selectedAdvanceForDisburse.advance_ref_no}
              </h3>
              <button onClick={() => setSelectedAdvanceForDisburse(null)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAdvanceDisburseSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Disbursed Amount (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  required
                  value={advanceDisburseForm.paid_amount}
                  onChange={(e) => setAdvanceDisburseForm({ ...advanceDisburseForm, paid_amount: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">UTR / Bank Transaction Reference *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. UTR1234567890"
                  value={advanceDisburseForm.payment_reference}
                  onChange={(e) => setAdvanceDisburseForm({ ...advanceDisburseForm, payment_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Disbursement Date *</label>
                <input
                  type="date"
                  required
                  value={advanceDisburseForm.payment_date}
                  onChange={(e) => setAdvanceDisburseForm({ ...advanceDisburseForm, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setSelectedAdvanceForDisburse(null)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingAdvanceDisburse}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md"
                >
                  {submittingAdvanceDisburse ? 'Disbursing...' : 'Confirm Disbursement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: TRAVEL EXTENSION */}
      {showExtendModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-black text-slate-900 text-base flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" /> Extend Travel Duration
              </h3>
              <button onClick={() => setShowExtendModal(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleExtendSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Current End Date</label>
                <div className="font-mono text-slate-600 font-bold">{request.end_date || 'N/A'}</div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Revised End Date *</label>
                <input
                  type="date"
                  required
                  min={request.end_date}
                  value={extendForm.revised_end_date}
                  onChange={(e) => setExtendForm({ ...extendForm, revised_end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Reason for Extension *</label>
                <textarea
                  required
                  rows="3"
                  placeholder="Explain why trip duration was extended..."
                  value={extendForm.reason}
                  onChange={(e) => setExtendForm({ ...extendForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Optional Additional Advance (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00 (Subject to 75% utilisation check)"
                  value={extendForm.additional_advance_amount}
                  onChange={(e) => setExtendForm({ ...extendForm, additional_advance_amount: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowExtendModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingExtend}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl text-xs shadow-md"
                >
                  {submittingExtend ? 'Submitting...' : 'Save Extension'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: CONSOLIDATED FINAL CLAIM SUBMISSION (TRAVELLER-ONLY) */}
      {showFinalClaimModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-black text-slate-900 text-base flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Consolidated Final Claim
              </h3>
              <button onClick={() => setShowFinalClaimModal(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Total Recorded Expenses:</span>
                  <span className="font-mono font-bold text-slate-900">₹ {(metrics?.total_expenses_recorded || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Total Advance Received:</span>
                  <span className="font-mono font-bold text-emerald-700">₹ {(metrics?.total_advance_received || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="border-t border-slate-200 pt-2 flex justify-between font-bold text-sm">
                  <span>{metrics && metrics.company_payable > 0 ? 'Company Reimbursement Payable:' : 'Employee Refund to Company:'}</span>
                  <span className={`font-mono ${metrics && metrics.company_payable > 0 ? 'text-teal-700' : 'text-rose-700'}`}>
                    ₹ {(metrics && metrics.company_payable > 0 ? metrics.company_payable : (metrics?.employee_refund || 0)).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 leading-relaxed">
                <span className="font-bold">Traveller Declaration:</span> I hereby certify that all daily travel expenses recorded under Travel ID <strong>{request.request_number}</strong> were incurred solely for official business purposes, supported by uploaded receipts.
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowFinalClaimModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleFinalClaimSubmit}
                  disabled={submittingFinalClaim}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold rounded-xl text-xs shadow-md"
                >
                  {submittingFinalClaim ? 'Submitting...' : 'Confirm & Submit Final Claim'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Preview Modal */}
      <AttachmentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        attachment={previewAttachment}
        title="Bill &amp; Receipt Preview"
      />
    </div>
  );
};

export default TravelRequestDetail;
